<template>
  <form @submit.prevent="handleSubmit">
    <slot />

    <!-- Required to allow the form to be submitted programmatically -->
    <button
      ref="btn"
      style="display:none"
      type="submit"
    />
  </form>
</template>

<script>
/**
 * This Form component helps automate form validation
 *
 *  It is a simplification of a more customized solution developed for another
 *  project and does not handle field values or the submit process.
 */
export default {
  // Provide a mechanism for field components to register themselves to the form
  provide: function() {
    return {
      registerFormField: this.registerFormField,
      unregisterFormField: this.unregisterFormField,
    }
  },
  data() {
    return {
      fields: [],
    }
  },
  methods: {
    /**
     * All fields contained in this form should register themselves in order
     * to be processed by the mechanism provided by this component.
     *
     * Note: every field should have a `validate`, `isValid`, `isDeleted`, `reset`, `resetValidation`, `getName` & `getValue` method.
     */
    registerFormField({ validate, isValid, resetValidation, getValue, getName, isDeleted, reset }) {
      this.fields.push({ validate, isValid, resetValidation, getValue, getName, isDeleted, reset })
    },
    unregisterFormField({ getName }) {
      let name = getName()
      if (name === '') return
      let index = this.fields.findIndex(field => field.getName() === name)
      if (index > -1) {
        this.fields.splice(index, 1)
      }
    },

    /**
     * Get the form data
     */
    getValues() {
      return this.fields.reduce((values, field) => {
        values[field.getName()] = field.getValue()
        return values
      }, {})
    },

    /**
     * Run the validation on every registered field
     */
    validate() {
      this.fields.forEach(field => {
        field.validate()
      })
    },
    /**
     * Are all registered fields valid?
     */
    isValid() {
      return this.fields.every(field => {
        return field.isDeleted() || field.isValid()
      })
    },
    /**
     * Reset the validation mechanism
     */
    resetValidation() {
      this.fields.forEach(field => {
        field.resetValidation()
      })
    },
    /**
     * Allow the form to be submitted programmatically
     */
    submit() {
      // Note:
      //  Why not ref the form itself and use submit() on the form DOM element?
      //  Because then the submit event handler bound by vue is not triggered...
      this.$refs.btn.click()
    },
    /**
     * Capture the submit event, handle validation, and then either pass on
     * the event on success, or trigger an error event instead.
     */
    handleSubmit(e) {
      this.validate()
      if (this.isValid()) {
        this.$emit('submit', e)
      } else {
        this.$emit('error', e)
      }
    },
    /**
     * Reset all form fields (includes a validation reset)
     */
    reset() {
      this.fields.forEach(field => {
        field.reset()
      })
    },
  },
}
</script>
